import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Template } from '../../models/Template';
import { PlaceholderService } from './placeholder.service';
import { Practice } from '../../models/Practice';
import { Client } from '../../models/Client';
import { Patient } from '../../models/Patient';
import { TemplateMergeField } from '../../models/TemplateMergeField';
import { MergeFieldContent } from '../../enums/merge-field-content';

@Injectable({
  providedIn: 'root'
})
export class ConversationService {
  public initialMessage = new UntypedFormControl('');
  public linkButton = new UntypedFormControl('');
  public quickReply = new UntypedFormControl('');
  public quickReply2 = new UntypedFormControl('');
  public practice?: Practice | null;
  public patient?: Patient | null;
  client?: Client;

  constructor(private placeholderService: PlaceholderService) {}

  private getMergeFieldReplacement(
    mergeField: TemplateMergeField,
    placeholders: { placeholderId: string; replacement: string }[]
  ): string {
    const userReplacement = placeholders.find(
      (p) => p.placeholderId === mergeField.id.toString()
    )?.replacement;
    
    if (userReplacement !== undefined && userReplacement.trim() !== '') {
      return userReplacement;
    }
    
    switch (mergeField.content) {
      case MergeFieldContent.PracticeName:
        return (
          this.practice?.name ||
          mergeField.placeholder
        );

      case MergeFieldContent.ClientName:
        return (
          this.client?.fullName ||
          mergeField.placeholder
        );
      case MergeFieldContent.ClientTitle:
        return (
          this.client?.title ||
          mergeField.placeholder
        );
  
      case MergeFieldContent.ClientFirstName:
        return (
          this.client?.firstName ||
          mergeField.placeholder
        );
  
      case MergeFieldContent.ClientLastName:
        return (
          this.client?.lastName ||
          mergeField.placeholder
        );

      case MergeFieldContent.ClientBalance:
        if (this.client?.accountStatus !== undefined && this.client?.accountStatus !== null) {
          const balance = parseFloat(this.client.accountStatus).toFixed(2);
          return `£${balance}`;
        }
        return mergeField.placeholder;

      case MergeFieldContent.PatientName:
        return (
          this.patient?.name || mergeField.placeholder
        );
      case MergeFieldContent.ClientPostcode:
        return (
          this.client?.postcode ||
          mergeField.placeholder
        );

      case MergeFieldContent.PracticeEmail:
        return (
          this.practice?.email ||
          mergeField.placeholder
        );

      case MergeFieldContent.PracticePhone:
        return (
          this.practice?.phoneNumber ||
          mergeField.placeholder
        );

      case MergeFieldContent.PracticeWebsite:
        return (
          this.practice?.websiteUrl ||
          mergeField.placeholder
        );
        
      case MergeFieldContent.AppointmentInformation:
        return (
          mergeField.placeholder
        );
        
      case MergeFieldContent.ProductInformation:
        return (
          placeholders.find(
            (p) => p.placeholderId === mergeField.id.toString()
          )?.replacement ||
          mergeField.placeholder
        );
        
      case MergeFieldContent.Custom:
        return (
          mergeField.placeholder
        );
    }

    return mergeField.placeholder;
  }

  getTemplateText(
    template: Template,
    placeholders: { placeholderId: string; replacement: string }[]
  ): string {
    let templateText = template.body;

    template.mergeFields.forEach((mergeField) => {
      templateText = templateText.replace(
        mergeField.placeholder,
        this.getMergeFieldReplacement(mergeField, placeholders)
      );
    });

    return templateText;
  }

  updatePreviewConversation(
    template: Template,
    placeholders: { placeholderId: string; replacement: string }[]
  ) {
    const text = this.placeholderService.replacePlaceholders(
      template.body ?? '',
      template.mergeFields,
      placeholders,
      this.practice,
      this.patient,
      this.client
    );
    this.initialMessage.setValue(text);

    const linkButton = this.placeholderService.replacePlaceholders(
      template.paymentButtonText ?? '',
      template.mergeFields,
      placeholders,
      this.practice,
      this.patient,
      this.client
    );
    this.linkButton.setValue(linkButton);

    const quickReply = this.placeholderService.replacePlaceholders(
      template.quickReply ?? '',
      template.mergeFields,
      placeholders,
      this.practice,
      this.patient,
      this.client
    );
    this.quickReply.setValue(quickReply);

    const quickReply2 = this.placeholderService.replacePlaceholders(
      template.quickReply2 ?? '',
      template.mergeFields,
      placeholders,
      this.practice,
      this.patient,
      this.client
    );
    this.quickReply2.setValue(quickReply2);
  }
}
